import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import {
  Trophy,
  TrendingUp,
  Wallet,
  Globe,
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  Briefcase,
  PiggyBank,
  Plane,
  Sparkles,
  ChevronRight,
  X
} from 'lucide-react';

const API_URL = window.location.origin;

// Icon mapping for dynamic rendering
const iconMap = {
  Trophy,
  TrendingUp,
  Wallet,
  Globe,
  CheckCircle2,
  GraduationCap,
  Briefcase,
  PiggyBank,
  Plane,
  Sparkles
};

// Default Journey/Persona data (fallback)
const defaultPersonas = [
  {
    id: 'course-known',
    name: 'I Know My Course',
    tagline: 'Help me find the right university',
    subtitle: 'You know what you want to study—let us find where',
    icon: GraduationCap,
    gradient: 'from-ux-cyan to-ux-blue',
    bgGlass: 'bg-ux-cyan/10',
    border: 'border-ux-cyan/30',
    accent: 'text-ux-cyan',
    accentBg: 'bg-ux-cyan',
    image: 'https://images.unsplash.com/photo-1562774053-701939374585?w=800',
    stats: { label: 'Universities', value: '500+' },
    description: "You've already decided your field. Now let's find universities that excel in your chosen course and match your profile.",
    journey: [
      { step: 'Course Match', desc: 'We analyze universities strong in your specific field', icon: GraduationCap },
      { step: 'Profile Fit', desc: 'Match your academics with university requirements', icon: CheckCircle2 },
      { step: 'Ranking Filter', desc: 'Filter by QS rankings, specializations & research output', icon: Trophy },
      { step: 'Apply Smart', desc: 'Strategic applications to maximize acceptance chances', icon: Briefcase },
    ],
    cta: 'Find Universities for My Course',
    features: ['Course-specific rankings', 'Faculty expertise matching', 'Research opportunities'],
  },
  {
    id: 'budget-clarity',
    name: 'I Need Budget Clarity',
    tagline: 'Show me affordable options',
    subtitle: 'Quality education within your financial comfort',
    icon: Wallet,
    gradient: 'from-emerald-400 to-teal-500',
    bgGlass: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    accent: 'text-emerald-500',
    accentBg: 'bg-emerald-500',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
    stats: { label: 'Savings', value: 'Up to 60%' },
    description: "Let's be real about costs. We'll find programs that fit your budget—including scholarships, loans, and work-while-study options.",
    journey: [
      { step: 'Budget Assessment', desc: 'Map your total budget including living costs', icon: PiggyBank },
      { step: 'Scholarship Finder', desc: 'Match with merit, need-based & country-specific aids', icon: Trophy },
      { step: 'Affordable Destinations', desc: 'Discover quality programs in budget-friendly countries', icon: Globe },
      { step: 'Loan & Work Options', desc: 'Education loans and part-time work possibilities', icon: Briefcase },
    ],
    cta: 'Show Affordable Options',
    features: ['Cost calculator', 'Scholarship matching', 'Loan guidance'],
  },
  {
    id: 'country-help',
    name: 'Help Me Choose a Country',
    tagline: 'Compare destinations side-by-side',
    subtitle: 'USA, UK, Canada, Australia—which is right for you?',
    icon: Globe,
    gradient: 'from-ux-violet to-ux-rose',
    bgGlass: 'bg-ux-violet/10',
    border: 'border-ux-violet/30',
    accent: 'text-ux-violet',
    accentBg: 'bg-ux-violet',
    image: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800',
    stats: { label: 'Destinations', value: '15+' },
    description: "Each country has its pros and cons. We'll help you compare costs, visa rules, work rights, and lifestyle to find your best fit.",
    journey: [
      { step: 'Compare Countries', desc: 'Side-by-side comparison of costs, culture & climate', icon: Globe },
      { step: 'Visa & Work Rights', desc: 'Understand post-study work options in each country', icon: CheckCircle2 },
      { step: 'Lifestyle Match', desc: 'Find countries that suit your preferences', icon: Plane },
      { step: 'PR Pathways', desc: 'Which countries offer permanent residency routes?', icon: TrendingUp },
    ],
    cta: 'Compare Destinations',
    features: ['Country comparison tool', 'Visa requirements', 'Cost of living data'],
  },
  {
    id: 'career-focus',
    name: 'I Care About Career Outcomes',
    tagline: 'Jobs, PR & future growth',
    subtitle: 'ROI-focused education for your career goals',
    icon: TrendingUp,
    gradient: 'from-amber-400 to-orange-500',
    bgGlass: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    accent: 'text-amber-500',
    accentBg: 'bg-amber-500',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800',
    stats: { label: 'Avg Salary', value: '$85K+' },
    description: "Education is an investment. Let's find programs with strong placement records, industry connections, and PR pathways.",
    journey: [
      { step: 'Career Goals', desc: 'Define your target industry and job roles', icon: Briefcase },
      { step: 'Placement Data', desc: 'Find universities with proven job placement records', icon: TrendingUp },
      { step: 'Industry Connect', desc: 'Programs with internships & company partnerships', icon: CheckCircle2 },
      { step: 'PR & Settlement', desc: 'Countries where you can build a long-term career', icon: Globe },
    ],
    cta: 'Show Career-Focused Programs',
    features: ['Placement statistics', 'Salary benchmarks', 'PR pathway guidance'],
  },
];

export const JourneySelector = () => {
  const { isDark } = useTheme();
  const [activePersona, setActivePersona] = useState(null);
  const [journeyPersonas, setJourneyPersonas] = useState(defaultPersonas);
  
  // Fetch journey settings from CMS
  useEffect(() => {
    const fetchJourneySettings = async () => {
      const res = await fetchWithRetry(`${API_URL}/api/cms/site-settings/journey`, {}, { label: 'journey-settings' });
      if (res && res.ok) {
        const data = await res.json();
        if (data.personas && data.personas.length > 0) {
          // Map CMS data to component format, with icon conversion
          const mappedPersonas = data.personas
            .filter(p => p.is_active !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(p => ({
              ...p,
              icon: iconMap[p.icon] || Trophy,
              stats: { label: p.stats_label, value: p.stats_value },
              journey: (p.journey_steps || []).map(step => ({
                ...step,
                icon: iconMap[step.icon] || CheckCircle2
              }))
            }));
          if (mappedPersonas.length > 0) {
            setJourneyPersonas(mappedPersonas);
          }
        }
      }
    };
    fetchJourneySettings();
  }, []);

  const handleCardClick = (personaId) => {
    setActivePersona(activePersona === personaId ? null : personaId);
  };

  const selectedPersona = journeyPersonas.find(p => p.id === activePersona);

  return (
    <section className={`py-16 md:py-24 relative overflow-hidden ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`} data-testid="journey-selector">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-ux-cyan/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-ux-violet/10 rounded-full blur-3xl" />

      <div className="container-max relative z-10 px-4 md:px-0">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <Badge className={`mb-4 ${isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-blue/10 text-ux-blue'}`}>
            Start Here
          </Badge>
          <h2 className={`font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            What's Your
            <span className="gradient-text-aurora"> Priority?</span>
          </h2>
          <p className={`text-base md:text-xl max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
            Choose what matters most to you. We'll personalize your study abroad journey accordingly.
          </p>
        </div>

        {/* Persona Cards Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 ${activePersona ? 'mb-8' : ''}`}>
          {journeyPersonas.map((persona) => {
            const IconComponent = persona.icon;
            const isActive = activePersona === persona.id;
            
            return (
              <div
                key={persona.id}
                onClick={() => handleCardClick(persona.id)}
                className={`group relative cursor-pointer rounded-3xl overflow-hidden transition-all duration-500 ${
                  isActive 
                    ? `ring-2 ring-offset-2 ${isDark ? 'ring-offset-[#233842]' : 'ring-offset-ux-slate-light'} ring-${(persona.accent || 'text-ux-cyan').replace('text-', '')}`
                    : 'hover:scale-[1.02]'
                } ${activePersona && !isActive ? 'opacity-50 scale-95' : ''}`}
                data-testid={`persona-${persona.id}`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img 
                    src={persona.image} 
                    alt={persona.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent`} />
                </div>

                {/* Content */}
                <div className="relative z-10 p-6 h-80 flex flex-col justify-end">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${persona.gradient || 'from-ux-cyan to-ux-blue'} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>

                  {/* Name & Tagline */}
                  <h3 className="font-display text-2xl font-bold text-white mb-1">
                    {persona.name}
                  </h3>
                  <p className="text-white/80 text-sm mb-2">{persona.tagline}</p>
                  <p className="text-white/60 text-xs">{persona.subtitle}</p>

                  {/* Stats Badge */}
                  <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${persona.bgGlass || 'bg-ux-cyan/10'} backdrop-blur-sm border ${persona.border || 'border-ux-cyan/30'} w-fit`}>
                    <span className={persona.accent || 'text-ux-cyan'}>{persona.stats?.value}</span>
                    <span className="text-white/70 text-sm">{persona.stats?.label}</span>
                  </div>

                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white flex items-center justify-center">
                      <CheckCircle2 className={`w-5 h-5 ${persona.accent || 'text-ux-cyan'}`} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Expanded Journey View */}
        {selectedPersona && (
          <div 
            className={`rounded-3xl overflow-hidden transition-all duration-500 animate-in fade-in slide-in-from-bottom-4 ${
              isDark ? 'bg-[#1a2830]/80 backdrop-blur-xl border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-gray-200'
            }`}
            data-testid="journey-details"
          >
            <div className="p-8 md:p-12">
              {/* Header */}
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${selectedPersona.gradient || 'from-ux-cyan to-ux-blue'} flex items-center justify-center shadow-lg`}>
                    <selectedPersona.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-display text-3xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      {selectedPersona.name}
                    </h3>
                    <p className={selectedPersona.accent || 'text-ux-cyan'}>{selectedPersona.tagline}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setActivePersona(null)}
                  className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <X className={`w-6 h-6 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`} />
                </button>
              </div>

              {/* Description */}
              <p className={`text-lg mb-8 max-w-3xl ${isDark ? 'text-white/80' : 'text-ux-charcoal/80'}`}>
                {selectedPersona.description}
              </p>

              {/* Journey Steps */}
              <div className="mb-8">
                <h4 className={`font-display text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Your Personalized Journey
                </h4>
                <div className="grid md:grid-cols-4 gap-4">
                  {(selectedPersona.journey || []).map((step, index) => {
                    const StepIcon = step.icon || CheckCircle2;
                    return (
                      <div 
                        key={index}
                        className={`relative p-5 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gray-50'} group hover:scale-[1.02] transition-all`}
                      >
                        {/* Step number */}
                        <div className={`absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-to-br ${selectedPersona.gradient || 'from-ux-cyan to-ux-blue'} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                          {index + 1}
                        </div>
                        
                        {/* Connector line */}
                        {index < 3 && (
                          <div className={`hidden md:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r ${selectedPersona.gradient || 'from-ux-cyan to-ux-blue'}`} />
                        )}

                        <div className={`w-10 h-10 rounded-xl ${selectedPersona.bgGlass || 'bg-ux-cyan/10'} flex items-center justify-center mb-3`}>
                          <StepIcon className={`w-5 h-5 ${selectedPersona.accent || 'text-ux-cyan'}`} />
                        </div>
                        <h5 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                          {step.step}
                        </h5>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                          {step.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Features & CTA */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-6 border-t border-white/10">
                <div className="flex flex-wrap gap-3">
                  {(selectedPersona.features || []).map((feature, i) => (
                    <Badge 
                      key={i} 
                      variant="outline" 
                      className={`${isDark ? 'border-white/20 text-white/80' : ''}`}
                    >
                      <CheckCircle2 className={`w-3 h-3 mr-1 ${selectedPersona.accent || 'text-ux-cyan'}`} />
                      {feature}
                    </Badge>
                  ))}
                </div>
                <Button 
                  size="lg"
                  className={`bg-gradient-to-r ${selectedPersona.gradient || 'from-ux-cyan to-ux-blue'} text-white rounded-full px-8 hover:opacity-90 hover:scale-105 transition-all shadow-lg`}
                  asChild
                >
                  <Link to={`/contact?motivation=${selectedPersona.id}`}>
                    {selectedPersona.cta || 'Talk to a Counsellor'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom CTA when no persona selected */}
        {!activePersona && (
          <div className="text-center mt-12">
            <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              Not sure which path is right for you?
            </p>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-ux-cyan to-ux-violet text-white rounded-full px-8 hover:opacity-90 hover:scale-105 transition-all"
              asChild
            >
              <Link to="/contact?motivation=ai-guided">
                <Sparkles className="mr-2 h-5 w-5" />
                Talk to Our Counsellors
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default JourneySelector;
